import React, { useRef, useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import Phaser from 'phaser';
import RescueRobotron from './game/gameLogic';
import GameUI from './ui/GameUI';
import DebugUI from './ui/DebugUI';

const FRAME_RATE = 30;
const FRAME_INTERVAL = 1000 / FRAME_RATE;

const Game = () => {
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const [showDebugUI, setShowDebugUI] = useState(false);
  const [, setFrame] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    // Phaser game configuration
    const config = {
      type: Phaser.AUTO,
      parent: containerRef.current,
      scale: {
        mode: Phaser.Scale.RESIZE, // Ensure the game resizes dynamically
        autoCenter: Phaser.Scale.CENTER_BOTH, // Center the game both horizontally and vertically
        width: window.innerWidth, // Set width dynamically
        height: window.innerHeight, // Set height dynamically
      },
      physics: {
        default: 'arcade',
        arcade: {
          debug: false,
        },
      },
      scene: RescueRobotron,
    };

    // Create the Phaser game instance
    const game = new Phaser.Game(config);
    gameRef.current = game;

    // Function to resize the game when the window resizes
    const resizeGame = () => {
      if (gameRef.current) {
        gameRef.current.scale.resize(window.innerWidth, window.innerHeight);
      }
    };

    // Add window resize listener
    window.addEventListener('resize', resizeGame);

    // Game update logic
    let lastTime = Date.now();
    let intervalId;

    const update = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      // Force a re-render by updating the frame state
      setFrame(prevFrame => prevFrame + 1);
    };

    intervalId = setInterval(() => {
      update(Date.now());
    }, FRAME_INTERVAL);

    // Cleanup function to destroy the game and remove the resize listener
    return () => {
      window.removeEventListener('resize', resizeGame);
      clearInterval(intervalId);
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{
      position: 'relative',
      width: '100vw',
      height: '80vh',
      overflow: 'hidden'
    }}>
      <div ref={containerRef} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100vw',
        height: '80vh'
      }} />
      {gameRef.current && (
        <>
          <GameUI gameRef={gameRef} />
          {showDebugUI && <DebugUI gameRef={gameRef} />}
          <Settings
            onClick={() => setShowDebugUI(!showDebugUI)}
            style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              width: '24px',
              height: '24px',
              cursor: 'pointer',
              color: 'white'
            }}
          />
        </>
      )}
    </div>
  );
};

export default Game;
